import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isEmpty, isEqual, isNil } from 'lodash';

import Spinner from 'components/spinner/Spinner';
import SVGIcon from 'components/svg-icon/SVGIcon';
import Overlay from 'components/overlay/Overlay';
import { withModal } from 'components/modal/ModalHOC';
import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import MentionCard from 'components/mention-card/mention-card/MentionCard';
import MentionCardModalAssetInstagram from 'components/mention-card/mention-card-modal-asset/channels/instagram/MentionCardModalAssetInstagram';
import MentionCardModalAssetYoutube from 'components/mention-card/mention-card-modal-asset/channels/youtube/MentionCardModalAssetYoutube';

import { deletePosts } from 'redux-store/reducers/instagramPostsDeleteReducer';

import { __t, applyTranslations } from 'utils/language';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { DEFAULT_PLACEHOLDER_IMAGE } from 'utils/image';
import { getPostsToDelete } from 'utils/posts';
import { FIXED_TO_TOP } from 'modules/insights/sov/utils/sov';

import './sov-posts.scss';

import type { ChannelTypes, MentionCardModalItem } from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));

type OwnProps = {
  posts: Array<MentionCardModalItem>,
  channel: ChannelTypes,
  isFreemium?: boolean
};

type StateProps = {
  isLoading: boolean,
  userChannelConsents: ConsentReducerConsentsMap
};

type DispatchProps = {
  dispatchDeleteInstagramPosts: (a: Array<string>) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const ImageWithModal = withModal(<div />, {
  ...WITH_GALLERY_MODAL_CONFIG,
  positionOverride: FIXED_TO_TOP
});

const PlaceholderWithModal = withModal((
  <ConsentPlaceholder
    withLearnMore
    type="instagram"
  />
), {
  ...WITH_GALLERY_MODAL_CONFIG,
  positionOverride: FIXED_TO_TOP
});

const SHARED_MSG_KEY = 'sov_shared';

class SOVPosts extends React.PureComponent<Props> {
  componentDidUpdate(prevProps: Props) {
    const { posts } = this.props;

    if (!isEqual(posts, prevProps.posts)) {
      this.deleteUnavailablePosts();
    }
  }

  deleteUnavailablePosts = (): void => {
    const { posts, dispatchDeleteInstagramPosts } = this.props;

    const postsWithCodes = posts.map(post => ({
      code: post.code || '',
      assetUrl: post.assetUrl
    }));

    const postsToDelete = getPostsToDelete(postsWithCodes);

    if (!isEmpty(postsToDelete)) {
      dispatchDeleteInstagramPosts(postsToDelete);
    }
  };

  renderYoutubePlayButton(): React.ReactNode {
    return (
      <div className="sov-posts__youtube-overlay">
        <SVGIcon
          name="youtube-play"
          width={43}
          height={31}
        />
      </div>
    );
  }

  renderModalAsset(selectedItem: MentionCardModalItem, currentPosition: number): React.ReactNode | undefined | null {
    const { channel, userChannelConsents } = this.props;

    if (channel === 'instagram') {
      return (
        <MentionCardModalAssetInstagram
          userChannelConsents={userChannelConsents}
          selectedItem={selectedItem}
          currentPosition={currentPosition}
        />
      );
    }

    if (channel === 'youtube') {
      return (
        <MentionCardModalAssetYoutube
          userChannelConsents={userChannelConsents}
          selectedItem={selectedItem}
          currentPosition={currentPosition}
        />
      );
    }

    return null;
  }

  renderModalTrigger = (url: string, index: number, is404: boolean): JSX.Element => {
    const { posts, channel, userChannelConsents } = this.props;
    const backgroundImageUrl = is404 ? DEFAULT_PLACEHOLDER_IMAGE : url;

    const modalContentProps = {
      theme: 'white',
      items: posts,
      currentPosition: index,
      className: 'sov-posts__modal',
      itemClassName: 'sov-posts__modal-item',
      renderCustomAsset: (selectedItem, currentPosition) => (
        this.renderModalAsset(selectedItem, currentPosition)
      )
    };

    if (!userChannelConsents[channel]) {
      return (
        <PlaceholderWithModal
          modalContentProps={modalContentProps}
          type={channel}
          link={posts[index]?.url}
        />
      );
    }

    return (
      <ImageWithModal
        modalContentProps={modalContentProps}
        className="sov-posts__modal-trigger"
        style={{ backgroundImage: `url(${backgroundImageUrl})` }}
      >
        {
          channel === 'youtube' &&
            this.renderYoutubePlayButton()
        }
      </ImageWithModal>
    );
  };

  renderAsset(post: MentionCardModalItem, index: number): React.ReactNode {
    const { channel } = this.props;
    const url = post.assetUrl || post.url || '';
    const is404 = isNil(post.assetUrl);

    const containerClasses = classnames('sov-posts__image', {
      [`sov-posts__image--channel-${channel}`]: Boolean(channel)
    });

    return (
      <div className={containerClasses}>
        {
          post && post.url && post.code &&
            this.renderModalTrigger(url, index, is404)
        }
      </div>
    );
  }

  renderPost = (post: MentionCardModalItem, index: number): JSX.Element => {
    const { channel, isFreemium } = this.props;
    const key = `sov-post-${index}-${post.code}`;

    return (
      <div
        key={key}
        id={key}
        className="sov-post"
      >
        <MentionCard
          post={post}
          channel={channel}
          viewType="grid"
        >
          {this.renderAsset(post, index)}
        </MentionCard>
        {
          isFreemium && (
            <Overlay
              parentId={key}
              text={__t(`${SHARED_MSG_KEY}.messages.available_in_paid_version`)}
              showOnHover
            />
          )
        }
      </div>
    );
  };

  renderPosts(): React.ReactNode {
    const { posts } = this.props;

    return posts.map(this.renderPost);
  }

  render(): React.ReactNode {
    const { isLoading } = this.props;

    return (
      <div className="sov-posts">
        {
          isLoading ?
            <div className="sov-post__spinner">
              <Spinner
                position="inline"
                size="small"
                color="light"
              />
            </div> :
            this.renderPosts()
        }
      </div>
    );
  }
}

const mapStateToProps = ({ sovPosts, consentReducer }): StateProps => ({
  isLoading: sovPosts.isLoading,
  userChannelConsents: consentReducer.consents
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeleteInstagramPosts: (posts: Array<string>) => (
    dispatch(deletePosts(posts, 'instagram'))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVPosts);
